// ============================================================
// useLiveUserName.ts
// ONE way to render a firm member's name LIVE. Many API rows carry a frozen
// copy of a user's name (case.assignedTo, file.uploader, activity.actorName,
// calendar assigneeName, document-search uploadedBy…). Rendering that copy
// means a rename only shows after a refetch/refresh.
//
// Instead, render by USER ID against the central sources, which are kept
// live:
//   • authUser        — you (updated by setAuthUser on your Settings save)
//   • firmUsers map   — everyone (patched by the 'user-updated' broadcast via
//                       applyUserDisplayUpdate; your own entry is synced by
//                       setAuthUser)
// and fall back to the row's snapshot only when the id isn't known (e.g. a
// former member, or before firmUsers has loaded).
//
// Returns a resolver (not a single value) so list renders can call it per
// row without breaking the rules of hooks.
// ============================================================
import { useCallback } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'

export type NameResolver = (userId: string | null | undefined, fallback?: string | null) => string

export const fullNameOf = (u: { firstName?: string | null; lastName?: string | null; email?: string | null } | null | undefined): string =>
  u ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || (u.email ?? '') : ''

export const useLiveUserName = (): NameResolver => {
  const authUser = useCasevoStore((s) => s.authUser)
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  return useCallback<NameResolver>((userId, fallback) => {
    if (userId) {
      if (authUser && authUser.id === userId) {
        const n = fullNameOf(authUser)
        if (n) return n
      }
      const f = firmUsers.find((u) => u.id === userId)
      if (f) {
        const n = fullNameOf(f)
        if (n) return n
      }
    }
    return fallback ?? ''
  }, [authUser, firmUsers])
}
